import { useRolodexEntriesCachedById } from 'features/knowledgeDocumentViewer'
import { TimeSeriesService, useTimeSeriesService } from 'features/timeSeries'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ChartTimeUnit } from 'types'

export interface SparklineDataPoint {
  time: number
  value: number
}

export interface InstrumentSparklineData {
  id: string
  piTag: string
  description: string
  unit?: string
  data: SparklineDataPoint[]
  currentValue: number
  previousValue: number
  lastMeasuredAt: Date
}

/** Datapoints entry data with optional unit field */
interface DatapointsData {
  unit?: string
  [key: string]: unknown
}

interface UseInstrumentTimeSeriesDataResult {
  sparklineData: InstrumentSparklineData[]
  isLoading: boolean
  error: string | null
}

/** Cache for time series data to avoid redundant API calls */
const dataCache = new Map<
  string,
  { data: InstrumentSparklineData[]; timestamp: number }
>()
const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes
const REQUEST_TIMEOUT_MS = 15000 // 15 seconds timeout
const HOURS_OF_HISTORY = 24 // Hours of data to fetch around the latest measurement

/**
 * Hook to fetch time series data for matched datapoints.
 * First gets the latest available measurements, then fetches historical data around that time.
 */
export function useInstrumentTimeSeriesData(
  datapointIds: string[],
  enabled: boolean = true
): UseInstrumentTimeSeriesDataResult {
  const { fetchLatestSensorValues, fetchTimeSeriesData } =
    useTimeSeriesService()
  const [sparklineData, setSparklineData] = useState<InstrumentSparklineData[]>(
    []
  )
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Limit to 3 datapoints and create stable cache key
  const limitedIds = useMemo(
    () => datapointIds.slice(0, 3),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [datapointIds.slice(0, 3).sort().join(',')]
  )
  const cacheKey = useMemo(() => limitedIds.sort().join(','), [limitedIds])

  // Fetch datapoint entries to get units
  const { data: datapointEntries } = useRolodexEntriesCachedById(
    enabled ? limitedIds : []
  )

  // Build map of entry ID to unit
  const unitMap = useMemo(() => {
    const map = new Map<string, string>()
    if (datapointEntries) {
      for (const entry of datapointEntries) {
        const data = entry.entry?.data as DatapointsData | undefined
        if (data?.unit) {
          map.set(entry.entry.id, data.unit)
        }
      }
    }
    return map
  }, [datapointEntries])

  useEffect(() => {
    if (!enabled || limitedIds.length === 0) {
      setSparklineData([])
      setIsLoading(false)
      return
    }

    // Check cache
    const cached = dataCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      setSparklineData(cached.data)
      setIsLoading(false)
      return
    }

    // Cancel any in-flight requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    const controller = new AbortController()
    abortControllerRef.current = controller

    setIsLoading(true)
    setError(null)

    const fetchData = async () => {
      try {
        // Step 1: Get latest measurements to find when data was last recorded
        const latestResponse = await fetchLatestSensorValues(limitedIds)

        if (controller.signal.aborted) return

        const sparklines: InstrumentSparklineData[] = []

        // Step 2: For each sensor with data, fetch historical data around that time
        for (const entryId of limitedIds) {
          const latestPoint = latestResponse[entryId]
          if (!latestPoint || latestPoint.value === null) continue

          const lastMeasuredAt = new Date(latestPoint.timestamp)
          const historyStart = new Date(
            lastMeasuredAt.getTime() - HOURS_OF_HISTORY * 60 * 60 * 1000
          )

          // Calculate interval to get ~30 points
          const intervalConfig = TimeSeriesService.getInterval(
            HOURS_OF_HISTORY * 60 * 60,
            30
          )

          try {
            const historyResponse = await fetchTimeSeriesData(
              {
                entry_ids: [entryId],
                start_datetime: historyStart.toISOString(),
                end_datetime: lastMeasuredAt.toISOString(),
                interval: intervalConfig.interval,
                interval_unit: intervalConfig.interval_unit as ChartTimeUnit,
              },
              { signal: controller.signal }
            )

            if (controller.signal.aborted) return

            const points = historyResponse[entryId] || []

            // Transform to sparkline data points
            const sparklinePoints: SparklineDataPoint[] = points
              .filter((p) => p.value !== null)
              .map((p) => ({
                time: new Date(p.timestamp).getTime(),
                value: p.value as number,
              }))
              .sort((a, b) => a.time - b.time)

            // Extract PI tag from entry ID (format: "site-PI_TAG")
            const piTag = entryId.includes('-')
              ? entryId.substring(entryId.indexOf('-') + 1)
              : entryId

            // Use sparkline values for change calculation (they're aggregated consistently)
            // firstValue = oldest point (left side of chart)
            // currentValue = newest point (right side of chart)
            const firstValue =
              sparklinePoints.length > 0
                ? sparklinePoints[0].value
                : latestPoint.value
            const currentValue =
              sparklinePoints.length > 0
                ? sparklinePoints[sparklinePoints.length - 1].value
                : latestPoint.value

            // Change compares first (oldest) to current (newest)
            // Down trend: current < first → negative change
            const previousValue = firstValue

            sparklines.push({
              id: entryId,
              piTag,
              description: latestPoint.measurement || piTag,
              unit: unitMap.get(entryId),
              data:
                sparklinePoints.length > 0
                  ? sparklinePoints
                  : [
                      {
                        time: lastMeasuredAt.getTime() - 3600000,
                        value: currentValue,
                      },
                      { time: lastMeasuredAt.getTime(), value: currentValue },
                    ],
              currentValue,
              previousValue,
              lastMeasuredAt,
            })
          } catch {
            // If history fetch fails, still show the latest value
            const piTag = entryId.includes('-')
              ? entryId.substring(entryId.indexOf('-') + 1)
              : entryId

            sparklines.push({
              id: entryId,
              piTag,
              description: latestPoint.measurement || piTag,
              unit: unitMap.get(entryId),
              data: [
                {
                  time: lastMeasuredAt.getTime() - 3600000,
                  value: latestPoint.value,
                },
                { time: lastMeasuredAt.getTime(), value: latestPoint.value },
              ],
              currentValue: latestPoint.value,
              previousValue: latestPoint.value,
              lastMeasuredAt,
            })
          }
        }

        // Update cache
        dataCache.set(cacheKey, { data: sparklines, timestamp: Date.now() })
        setSparklineData(sparklines)
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return
        }
        console.error('Failed to fetch instrument time series data:', err)
        setError(err instanceof Error ? err.message : 'Failed to fetch data')
        setSparklineData([])
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false)
        }
      }
    }

    // Add timeout wrapper
    const timeoutId = setTimeout(() => {
      if (abortControllerRef.current === controller) {
        controller.abort()
        setError('Request timeout')
        setIsLoading(false)
      }
    }, REQUEST_TIMEOUT_MS)

    fetchData().finally(() => clearTimeout(timeoutId))

    return () => {
      clearTimeout(timeoutId)
      controller.abort()
    }
  }, [
    cacheKey,
    enabled,
    fetchLatestSensorValues,
    fetchTimeSeriesData,
    limitedIds,
    unitMap,
  ])

  return { sparklineData, isLoading, error }
}
